/**
 * Every campaign, most recent first (PLAN §12.6).
 *
 * The one column worth explaining is **SMS**: it shows the provider's count
 * once there is one, and the panel's estimate until then. Where the two
 * disagree the provider is right — it is what the bill is calculated from — so
 * the disagreement is marked rather than hidden. A panel that quietly showed
 * its own number would be a panel that under-reports what a campaign cost.
 */

import { el, moment, number, replace, type Child } from '../dom.ts';
import { campaignAuthors, campaigns, type CampaignRow } from '../data.ts';
import { translate as t } from '../i18n.ts';
import { failure, link, loading, renderShell } from '../shell.ts';
import { STATUS_LABELS, statusPill } from './status-words.ts';
import { partsShown } from './status-view.ts';

export function renderHistory(root: Element, email: string): void {
  const body = el('section', { class: 'card' }, el('h1', {}, t('history.title')), loading());
  renderShell(root, { active: 'campaigns', email, title: t('history.title') }, body);

  const load = () => {
    replace(body, el('h1', {}, t('history.title')), loading());
    void campaigns()
      .then(async (rows) => {
        // A second read rather than a join, because `auth.users` is not
        // readable by a client and should not become so: `campaign_authors`
        // hands back an address per campaign and nothing else about the
        // account. Allowed to fail on its own — a history with a blank column
        // is worth more than no history at all.
        const authors = rows.length === 0 ? [] : await campaignAuthors(rows.map((row) => row.id)).catch(() => []);
        const byId = new Map(authors.map((author) => [author.campaign_id, author.email]));

        replace(body, el('h1', {}, t('history.title')), table(rows, byId));
      })
      .catch(() => replace(body, el('h1', {}, t('history.title')), failure(load)));
  };

  load();
}

function table(rows: readonly CampaignRow[], authors: ReadonlyMap<number, string>): Child {
  if (rows.length === 0) return el('p', { class: 'muted' }, t('history.empty'));

  // Wrapped, because a narrow window should scroll the table rather than the
  // page — the same treatment every other table in the panel gets.
  return el(
    'div',
    { class: 'table-wrap' },
    el(
    'table',
    {},
    el(
      'thead',
      {},
      el(
        'tr',
        {},
        el('th', {}, t('history.id')),
        el('th', {}, t('history.when')),
        el('th', {}, t('history.status')),
        el('th', {}, t('history.author')),
        el('th', { class: 'numeric' }, t('history.recipients')),
        el('th', { class: 'numeric' }, t('history.parts')),
        el('th', {}),
      ),
    ),
    el('tbody', {}, ...rows.map((one) => row(one, authors.get(one.id)))),
    ),
  );
}

function row(campaign: CampaignRow, author: string | undefined): Child {
  const href = `/campaign/${campaign.id}`;

  return el(
    'tr',
    {},
    el('td', {}, `#${campaign.id}`),
    el('td', {}, moment(campaign.created_at)),
    el('td', {}, el('span', { class: statusPill(campaign.status) }, t(STATUS_LABELS[campaign.status] ?? 'status.draft'))),
    // `created_by` has been recorded since the first migration and never shown.
    // Everyone signed in already sees every campaign, so naming the colleague
    // who sent one adds no reach — it removes a question that otherwise needs a
    // database query.
    el('td', { class: 'author' }, author ?? t('history.authorUnknown')),
    el('td', { class: 'numeric' }, number(campaign.recipient_count)),
    el('td', { class: 'numeric' }, parts(campaign)),
    el('td', {}, el('a', { href, onclick: link(href) }, t('history.open'))),
  );
}

function parts(campaign: CampaignRow): Child {
  const shown = partsShown(campaign);
  if (!shown.disagrees) return number(shown.parts);

  // The provider's number wins, and the estimate is kept beside it so the
  // difference is visible rather than merely absent.
  return el(
    'span',
    { class: 'parts-disagree', title: t('history.partsDisagree') },
    number(shown.parts),
    el('small', {}, ` (${number(shown.estimate)})`),
  );
}
